import { useEffect } from "react";

/**
 * Lightweight SEO helper — no react-helmet, no extra deps.
 * Updates <title>, meta description, robots, canonical and Open Graph tags
 * directly on the document head. Restores the previous title on unmount.
 *
 * Usage:
 *   useSeo({
 *     title: "Privacy Policy – QuickDukaan",
 *     description: "How QuickDukaan collects and protects your data.",
 *     path: "/privacy-policy",
 *   });
 */
export function useSeo({ title, description, path, image, type = "website" } = {}) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
      return el;
    };

    const desc = description || "QuickDukaan is a hyperlocal quick-commerce platform helping local shopkeepers sell online with instant delivery.";
    setMeta('meta[name="description"]', { name: "description", content: desc });
    setMeta('meta[name="robots"]', { name: "robots", content: "index, follow" });

    // Open Graph
    setMeta('meta[property="og:title"]', { property: "og:title", content: title || "QuickDukaan" });
    setMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    setMeta('meta[property="og:type"]', { property: "og:type", content: type });
    if (image) setMeta('meta[property="og:image"]', { property: "og:image", content: image });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "QuickDukaan" });

    // Twitter
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title || "QuickDukaan" });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });

    // Canonical
    if (path) {
      let canonical = document.head.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", `https://quickdukaan.in${path}`);
      const og = setMeta('meta[property="og:url"]', { property: "og:url", content: `https://quickdukaan.in${path}` });
      void og;
    }

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, path, image, type]);
}
